/**
 * useHaptic — uniform haptic feedback across Telegram WebApp + browser.
 *
 * Priority:
 *   1. Telegram WebApp HapticFeedback (when running inside TG client)
 *   2. navigator.vibrate (Android browser fallback, iOS Safari ignores)
 *   3. No-op (desktop, unsupported browsers)
 *
 * Each call is swallowed if the underlying API throws — haptics are a
 * polish enhancement, never a blocker.
 */
import { useCallback } from "react";
import { getTg } from "../../useTelegramWebApp";

export type HapticImpact = "light" | "medium" | "heavy" | "rigid" | "soft";
export type HapticNotification = "error" | "success" | "warning";

/** Vibrate durations (ms) per impact style for navigator.vibrate fallback. */
const VIBRATE_FALLBACK: Record<HapticImpact, number> = {
  light: 10,
  medium: 20,
  heavy: 35,
  rigid: 15,
  soft: 8,
};

export function useHaptic() {
  const impact = useCallback((style: HapticImpact = "light") => {
    const tg = getTg();
    if (tg) {
      try {
        tg.HapticFeedback.impactOccurred(style);
        return;
      } catch {
        // silent: Telegram API absent or restricted; fall through to vibrate
      }
    }
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(VIBRATE_FALLBACK[style]);
      } catch {
        // silent: vibration blocked by user agent
      }
    }
  }, []);

  const notification = useCallback((type: HapticNotification) => {
    const tg = getTg();
    if (tg) {
      try {
        tg.HapticFeedback.notificationOccurred(type);
        return;
      } catch {
        // silent: see impact()
      }
    }
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      const pattern = type === "error" ? [10, 40, 10] : type === "success" ? [25] : [15];
      try {
        navigator.vibrate(pattern);
      } catch {
        // silent: vibration blocked
      }
    }
  }, []);

  const selection = useCallback(() => {
    const tg = getTg();
    if (tg) {
      try {
        tg.HapticFeedback.selectionChanged();
        return;
      } catch {
        // silent
      }
    }
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(5);
      } catch {
        // silent
      }
    }
  }, []);

  return { impact, notification, selection };
}
